/* eslint-disable @typescript-eslint/camelcase */
import axios, { AxiosResponse } from 'axios';
import * as R from 'ramda';
import { DeepPartial } from 'redux';

import { proxy } from 'services/shared';
import {
  REDUX_CLIENTCODE,
  REDUX_SERVICE_ENDPOINTS,
  REDUX_SESSIONKEY,
} from 'constants/persistence';
import { getServiceEndpoints } from 'services/ErplyAPI/getServiceEndpoints';
import { PatchScript } from 'containers/Forms/Settings/views/Debug/components/ReceiptSchema/Editor/schema/comms';
import { ExistingPaymentIntegrations } from 'constants/CAFA';
import { ObfuscatedLocalStorage } from 'utils/ObfuscatedLocalStorage';

type TableName = 'inv_rows' | 'payment';
export type InvoiceRowRecord = {
  parentRowID: number;
  returnTags: string[] | [];
  notes: string | '';
  serialNumber: string | '';
};

export type CreditInvoiceRecord = {
  creditedStableRowID: number;
};
export type JsonAPISuccessResponse = {
  id: number;
  json_object: {
    BrazilPOS: InvoiceRowRecord;
    CreditInvoice: CreditInvoiceRecord;
  };
};

export type JsonAPIPaymentRecord = {
  id: number;
  json_object: {
    terminalResponse: {
      brazilMeta: {
        merchantPatchScript?: PatchScript;
        cardholderPatchScript?: PatchScript;
        brazilVersion?: string;
        integrationName?: ExistingPaymentIntegrations;
      };
      /** Full response from the provider/ms */
      originalResponse?: any;
    };
  };
};

type JDocReturnTypePerTable = {
  inv_rows: JsonAPISuccessResponse['json_object'];
  payment: JsonAPIPaymentRecord['json_object'];
};

export const emptyJdocForType: {
  [K in TableName]: JDocReturnTypePerTable[K];
} = {
  inv_rows: {
    CreditInvoice: {
      creditedStableRowID: 0,
    },
    BrazilPOS: {
      parentRowID: 0,
      returnTags: [],
      notes: '',
      serialNumber: '',
    },
  },
  payment: {
    terminalResponse: {
      brazilMeta: {},
    },
  },
};

export type JsonAPIErrorResponse = {
  error: string;
};

type JsonGetResponse<T> = T extends 'inv_rows'
  ? JsonAPISuccessResponse
  : JsonAPIPaymentRecord;

type JsonSetPayload<T> = { id: number } & DeepPartial<
  T extends 'inv_rows' ? JsonAPISuccessResponse : JsonAPIPaymentRecord
>;

const getJsonApiUrl = async () => {
  const { url } = JSON.parse(
    localStorage.getItem(REDUX_SERVICE_ENDPOINTS) || '{}',
  )?.json;
  if (url) {
    return url;
  }
  const [payload] = await getServiceEndpoints();
  return payload?.json?.url;
};

async function getRecordsForRowPerTable<T extends TableName>({
  id,
  tableName,
}: {
  id: number;
  tableName: T;
}): Promise<AxiosResponse<JsonGetResponse<T>>> {
  const jsonApiUrl = await getJsonApiUrl();
  const clientCode = JSON.parse(
    ObfuscatedLocalStorage.getItem(REDUX_CLIENTCODE) || '""',
  );
  const sessionKey = JSON.parse(
    ObfuscatedLocalStorage.getItem(REDUX_SESSIONKEY) || '""',
  );
  return axios.get<JsonGetResponse<T>>(
    `${proxy}${jsonApiUrl}v1/json_object/${tableName}/${id}`,
    {
      headers: { clientCode, sessionKey },
    },
  );
}

async function saveRecordsForRowPerTable<T extends TableName>({
  recordToSave,
  tableName,
}: {
  recordToSave: JsonSetPayload<T>;
  tableName: T;
}): Promise<undefined | AxiosResponse<JsonGetResponse<T>>> {
  const emptyPayload = {
    id: recordToSave.id,
    json_object: emptyJdocForType[tableName],
  };

  const existingData =
    (await getRecordsForRowPerTable({ id: recordToSave.id, tableName })
      .then(res => res.data)
      .catch(() => ({} as JsonGetResponse<T>))) ?? emptyPayload;

  // Ignoring null / undefined, merge deep in the following priorities:
  // 1. Record to save
  // 2. Response
  // 3. Defaults
  // recordToSave may always be filled with undefineds
  const merged: JsonGetResponse<T> = R.reduce(
    R.mergeDeepWith((a, b) => b ?? a),
    emptyPayload,
    [existingData, recordToSave],
  );

  const isUnmodified = R.equals(merged, existingData);
  if (isUnmodified) {
    return undefined;
  }

  const jsonApiUrl = await getJsonApiUrl();
  const clientCode = JSON.parse(
    ObfuscatedLocalStorage.getItem(REDUX_CLIENTCODE) || '""',
  );
  const sessionKey = JSON.parse(
    ObfuscatedLocalStorage.getItem(REDUX_SESSIONKEY) || '""',
  );

  return axios.put<JsonGetResponse<T>>(
    `${proxy}${jsonApiUrl}v1/json_object/${tableName}/${merged.id}`,
    merged,
    {
      headers: { clientCode, sessionKey },
    },
  );
}

/**
 * Get record from Json Api based on recordID - stableRowID in sales document
 * @param recordID
 */
export const getRecordsForInvoiceRow = async ({ id }: { id: number }) => {
  return getRecordsForRowPerTable({ id, tableName: 'inv_rows' });
};
/**
 * Save record to Json Api
 * 1. Tries to fetch record
 * 2. If the record exists, modifies according to the payload
 * 3. If the payload is matching the returned record, ignores the save
 * 4. If record does not exist, saves new record
 */
export const saveRecordsToInvoiceRow = async (
  recordToSave: DeepPartial<JsonAPISuccessResponse> & { id: number },
) => {
  return saveRecordsForRowPerTable({ recordToSave, tableName: 'inv_rows' });
};

export const getRecordsForPaymentRow = async ({ id }: { id: number }) => {
  return getRecordsForRowPerTable({ id, tableName: 'payment' });
};

export const saveRecordsToPaymentRow = async (
  recordToSave: JsonAPIPaymentRecord,
) => {
  return saveRecordsForRowPerTable({ recordToSave, tableName: 'payment' });
};

export const getMultiRecordsForInvoiceRows = (recordIDs: number[]) => {
  const requests = recordIDs.map(r => getRecordsForInvoiceRow({ id: r }));
  return Promise.all(requests).then(data => data.map(({ data }) => data));
};
